import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);

  const setUserSafe = useCallback((u) => {
    userRef.current = typeof u === 'function' ? u(userRef.current) : u;
    setUser(u);
  }, []);

  // ── Charge le profil depuis la table users ────────────────
  const loadProfile = useCallback(async (authUser) => {
    if (!authUser) { setUserSafe(null); return; }

    // Timeout de sécurité : si Supabase ne répond pas en 8s, on utilise le fallback
    const fallbackUser = { id: authUser.id, email: authUser.email, name: authUser.email, role: 'utilisateur' };

    try {
      const fetchProfile = async () => {
        // Chercher par ID (compatible RLS : users.id = auth.uid())
        let { data } = await supabase
          .from('users')
          .select('id, username, email, name, role, service, active, locked, photo, last_login')
          .eq('id', authUser.id)
          .maybeSingle();

        // Fallback email pour les anciens comptes
        if (!data && authUser.email) {
          const fb = await supabase
            .from('users')
            .select('id, username, email, name, role, service, active, locked, photo, last_login')
            .eq('email', authUser.email)
            .maybeSingle();
          data = fb.data;
        }
        return data;
      };

      const timeout = new Promise(resolve => setTimeout(() => resolve(null), 8000));
      const data = await Promise.race([fetchProfile(), timeout]);

      if (data) {
        setUserSafe(data);
      } else if (!userRef.current) {
        setUserSafe(fallbackUser);
      }
    } catch {
      if (!userRef.current) {
        setUserSafe(fallbackUser);
      }
    }
  }, [setUserSafe]);

  useEffect(() => {
    // Failsafe absolu : forcer loading=false après 10s quoi qu'il arrive
    const globalTimeout = setTimeout(() => setLoading(false), 10000);

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (session?.user) {
          loadProfile(session.user).finally(() => {
            clearTimeout(globalTimeout);
            setLoading(false);
          });
        } else {
          clearTimeout(globalTimeout);
          setLoading(false);
        }
      })
      .catch(() => {
        clearTimeout(globalTimeout);
        setLoading(false);
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        await loadProfile(session.user);
      } else {
        setUserSafe(null);
      }
      setLoading(false);
    });

    return () => {
      clearTimeout(globalTimeout);
      subscription.unsubscribe();
    };
  }, [loadProfile, setUserSafe]);

  // ── Login ─────────────────────────────────────────────────
  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      const msg = error.message === 'Invalid login credentials'
        ? 'Email ou mot de passe incorrect'
        : error.message;
      throw new Error(msg);
    }

    await loadProfile(data.user);
    const profile = userRef.current;

    // Vérifier statut du compte (actif / non verrouillé)
    if (profile && (profile.active === false || profile.locked === true)) {
      await supabase.auth.signOut();
      setUserSafe(null);
      throw new Error(
        profile.locked
          ? 'Compte verrouillé. Contactez l\'administrateur.'
          : 'Compte désactivé. Contactez l\'administrateur.'
      );
    }

    // Mise à jour last_login + audit (via ID pour respecter la RLS)
    if (profile?.id) {
      supabase.from('users').update({ last_login: new Date().toISOString() }).eq('id', profile.id).then(() => {});
    }
    if (profile?.id) {
      supabase.from('audit_logs').insert({
        id: `login_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        user_id: profile.id,
        action: 'LOGIN',
        object: 'Session',
        new_value: profile.name || profile.username || email,
      }).then(() => {});
    }

    return data.user;
  };

  // ── Logout ────────────────────────────────────────────────
  const logout = async () => {
    await supabase.auth.signOut();
    setUserSafe(null);
  };

  const updateUser = (updates) => {
    setUserSafe(prev => ({ ...prev, ...updates }));
  };

  // ── Vérification des rôles (admin bypass automatique) ─────
  const hasRole = (...roles) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'super_admin') return true;
    return roles.includes(user.role);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasRole, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
